import { useState, useEffect, useRef, useCallback } from 'react';
import Article from "./Article";
import { useNavigate, useLocation } from "react-router-dom";
import { formatForDisplay } from './utils';
import useAxiosPrivate from "./useAxiosPrivate";
import useAuth from "./useAuth";

const Users = ({ statsData, onViewDetails, daysSinceSaved }) => {
    const [items, setItems] = useState([]);
    const [lastId, setLastId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [filters, setFilters] = useState({
        referral_code: "",
    });
    const observer = useRef();
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth();

    const location = useLocation();
    const navigate = useNavigate();

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const url = `/numbers?limit=10${lastId ? `&last_id=${lastId}` : ""}${daysSinceSaved !== undefined ? `&days_since_saved=${daysSinceSaved}` : ""}${filters.referral_code ? `&referral_code=${filters.referral_code}` : ""}`;
            const response = await axiosPrivate.get(url, {headers: {Authorization: "Bearer " + auth.accessToken}});
            setItems(prevItems => [...prevItems, ...response.data]);
            setHasMore(response.data.length > 0);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [axiosPrivate, auth.accessToken, lastId, daysSinceSaved, filters.referral_code]);

    useEffect(() => {
        setItems([]);
        setLastId(null);
    }, [location]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const lastItemRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setLastId(node.id);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    const downloadExcel = async () => {
        try {
            const queryParams = new URLSearchParams();
            if (lastId) queryParams.append("last_id", lastId);
            if (daysSinceSaved !== undefined) queryParams.append("days_since_saved", daysSinceSaved);
            if (filters.referral_code) queryParams.append("referral_code", filters.referral_code);
            const url = `/numbers_csv?${queryParams.toString()}`;
            const response = await axiosPrivate.get(url, {headers: {Authorization: "Bearer " + auth.accessToken}});
            const href = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
            const link = document.createElement("a");
            link.href = href;
            link.setAttribute("download", 'numbers.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        } catch(err) {
            console.log(err);
        }
    };

    const updateFilters = (evt) => {
        const { name, value } = evt.target;
        setFilters(curr => ({...curr, [name]: value}));
        setLastId(null);
        setItems([]);
        setHasMore(true);
    }

    const selectedArticle = statsData.find(
        article => article.link === location.pathname
    );

    const selectedArticleRef = useRef(null);

    useEffect(() => {
        if (selectedArticleRef.current) {
            selectedArticleRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, []);

    if (!selectedArticle) {
        return <div>Article not found</div>;
    }

    return (
        <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-1/4">
                <div className="flex overflow-x-auto md:overflow-x-hidden md:overflow-y-auto md:max-h-screen md:h-screen md:pb-32 md:pr-4 md:grid md:grid-cols-1 gap-6 pb-4 md:pb-0">
                    {statsData.map((article, index) => (
                        <Article
                            key={index}
                            title={article.title}
                            value={article.value}
                            isObject={article.isObject}
                            isSelected={selectedArticle.title === article.title}
                            onViewDetails={() => onViewDetails(article)}
                            ref={selectedArticle.title === article.title ? selectedArticleRef : null}
                        />
                    ))}
                </div>
            </div>
            <div className="w-full md:w-3/4">
                <div className="rounded-lg border border-gray-200 bg-slate-100 p-6 sticky top-6">
                    <h2 className="text-2xl font-medium text-gray-900">{selectedArticle.title}</h2>
                    <p className="text-sm text-gray-500">Details for {selectedArticle.title}</p>
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                            <div>
                                <h3 className="text-lg font-medium text-gray-800">Active WhatsApp Users</h3>
                                <p className="text-sm text-gray-500">
                                    {items.length} active user{items.length !== 1 ? 's' : ''} currently viewing this article
                                </p>
                                <div className="flex flex-wrap gap-4 mt-2">
                                    <div className="w-full md:w-auto">
                                        <label className="block text-sm font-medium text-gray-700">Referral Code</label>
                                        <input
                                            type="text"
                                            name="referral_code"
                                            className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                                            value={filters.referral_code}
                                            onChange={updateFilters}
                                        />
                                    </div>
                                </div>
                            </div>
                            {items && items.length > 0 && (
                                <button 
                                    onClick={downloadExcel}
                                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                                >
                                    Download Excel
                                </button>
                            )}
                        </div>

                        {items && items.length > 0 ? (
                            <div className="overflow-hidden">
                                <div className="max-h-80 overflow-y-auto">
                                    <table className="min-w-full">
                                        <thead className="bg-gray-50 sticky top-0 z-10">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    #
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    WhatsApp Number
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Policy Number
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Referral Code
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    First Seen
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Last Seen
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {items.map((item, index) => {
                                                if (items.length === index + 1) {
                                                    return (
                                                        <tr id={item.id} ref={lastItemRef} key={index} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {index + 1}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {formatForDisplay(item.wa_id)}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {item.policy_number}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {item.referral_code || "-"}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {item.created_at}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {item.last_seen}
                                                            </td>
                                                        </tr>
                                                    )
                                                } else {
                                                    return (
                                                        <tr id={item.id} key={index} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {index + 1}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {formatForDisplay(item.wa_id)}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {item.policy_number}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {item.referral_code || "-"}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {item.created_at}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {item.last_seen}
                                                            </td>
                                                        </tr>
                                                    )
                                                }
                                            })}
                                        </tbody>
                                    </table>
                                    {loading && <div>Loading...</div>}
                                    {!hasMore && <div>No more items to load</div>}
                                </div>
                            </div>
                            ) : (
                            <div className="text-center py-8 text-gray-500">No active users found</div>
                        )}
                    </div>
                    <button onClick={() => navigate("/dashboard")} className="mt-4 inline-block text-blue-600 hover:underline">
                        ← Back to List
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Users;
